import axios from 'axios';
import { authService } from './authService';
import { i18n } from '@/i18n';

// API-Konfiguration
type ApiEnvironment = 'PROD' | 'TEST' | 'LOCAL';
const API_ENV = (import.meta.env.VITE_API_ENV || 'TEST') as ApiEnvironment;

// API-URLs
const API_URLS = {
  PROD: import.meta.env.VITE_API_PROD_URL,
  TEST: import.meta.env.VITE_API_TEST_URL,
  LOCAL: import.meta.env.VITE_API_LOCAL_URL
};

// Aktuelle API-URL
const currentApiUrl = '/api'; // Immer relativ, Proxy kümmert sich um die richtige Weiterleitung

// Styling für Konsolenausgaben
const styles = {
  prod: 'background: #4caf50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
  test: 'background: #ff9800; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
  local:
    'background: #2196f3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
  info: 'color: #666; font-size: 12px;'
};

// API Client erstellen
const apiClient = axios.create({
  baseURL: currentApiUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Funktion zum Wechseln der API-Umgebung
export const switchApiEnvironment = (env: ApiEnvironment) => {
  // Setze die Umgebungsvariable
  window.localStorage.setItem('API_ENV', env);
  // Neuladen der Seite, damit der Proxy die neue Umgebung verwendet
  window.location.reload();
};

// Funktion zum Anzeigen der aktuellen API-Umgebung
const logApiEnvironment = () => {
  const targetUrl = API_URLS[API_ENV];
  const apiType = targetUrl.includes('ceetrac.de')
    ? targetUrl.includes('test.')
      ? 'TEST-API'
      : 'PRODUKTIONS-API'
    : 'LOKALE API';

  const styleType = targetUrl.includes('ceetrac.de')
    ? targetUrl.includes('test.')
      ? styles.test
      : styles.prod
    : styles.local;

  console.log('\n=== CEETrac API Status ===');
  console.log('%cAktive Umgebung:', styles.info);
  console.log(`%c${apiType}`, styleType);
  console.log('Ziel-URL:', targetUrl);
  console.log('========================\n');
};

// Request Interceptor für Authorization und Mandant
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    const mandant = authService.getMandant();

    // Token-Validierung
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Mandanten-Validierung
    if (!mandant && !config.url?.includes('/account/login')) {
      console.error('Kein Mandant gefunden - Request wird abgebrochen');
      return Promise.reject(new Error('Mandant ist erforderlich'));
    }

    // Setze Mandanten-Header
    config.headers['X-Mandant'] = mandant?.toUpperCase();

    // Setze Sprach-Header zentral aus der aktuellen UI-Sprache
    config.headers['Accept-Language'] = i18n.global.locale.value;

    // Validiere Mandanten-Header
    if (!config.headers['X-Mandant']) {
      console.error('X-Mandant Header konnte nicht gesetzt werden');
      return Promise.reject(new Error('X-Mandant Header ist erforderlich'));
    }

    // Debug-Logging für kritische API-Requests
    if (
      config.url?.includes('/abholungen') ||
      config.url?.includes('/einlieferungen') ||
      config.url?.includes('/ablieferungen')
    ) {
      console.log('🔍 API Request Debug:');
      console.log('  URL:', config.url);
      console.log('  Method:', config.method?.toUpperCase());
      console.log(
        '  Authorization Header:',
        config.headers.Authorization
          ? 'Bearer ***' + String(config.headers.Authorization).slice(-20)
          : 'MISSING'
      );
      console.log('  X-Mandant Header:', config.headers['X-Mandant']);
      console.log('  Token valid:', !!token);
      console.log('  Token length:', token?.length || 0);

      // JWT Token Claims Debug
      if (token) {
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));

            // Prüfe Role Claims Format
            if (payload.role) {
              const roleClaims = Array.isArray(payload.role) ? payload.role : [payload.role];
              roleClaims.forEach((claim: unknown, index: number) => {
                console.log(`    - role[${index}]:`, claim);
                if (typeof claim === 'string' && claim.includes('|')) {
                  const [claimMandant, permissions] = claim.split('|');
                  console.log(`      → Mandant: ${claimMandant}, Permissions: ${permissions}`);
                  console.log(
                    `      → Matches X-Mandant Header: ${claimMandant.toUpperCase() === config.headers['X-Mandant']}`
                  );
                }
              });
            }
          }
        } catch (e: unknown) {
          const errorMessage = e instanceof Error ? e.message : 'Unknown error';
          console.log('  JWT Token parsing failed:', errorMessage);
        }
      }
    }

    return config;
  },
  (error) => {
    console.error('Request Interceptor Fehler:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor für Error Handling
apiClient.interceptors.response.use(
  (response) => {
    // Validiere Mandanten-Header in der Antwort
    if (!response.config.headers['X-Mandant'] && !response.config.url?.includes('/account/login')) {
      console.error('Response ohne Mandanten-Header empfangen');
      return Promise.reject(new Error('Ungültige API-Antwort: Fehlender Mandanten-Header'));
    }
    return response;
  },
  async (error) => {
    // Spezielle Behandlung von Mandanten-bezogenen Fehlern
    if (error.response?.status === 400 && error.response?.data?.message?.includes('mandant')) {
      console.error('Mandanten-Fehler:', error.response.data.message);
      // Optional: Weiterleitung zur Mandantenauswahl
      window.location.href = '/mandant-selection';
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      await authService.logout();
      const currentPath = window.location.pathname;
      const redirectPath = currentPath === '/login' ? '/' : currentPath;
      window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`;
    }

    // Debug-Logging nur im Development Mode
    if (import.meta.env.DEV) {
      console.error('❌ Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.message,
        mandant: error.config?.headers?.['X-Mandant']
      });
    }

    return Promise.reject(error);
  }
);

// Initial API Environment Log
logApiEnvironment();

export { apiClient };
